import type { Action, Agent, Hook } from '@lucidtech/las-sdk-browser';
import { useCallback } from 'react';

import {
  toActionId,
  toAgentId,
  toHookId,
  useDeleteAction,
  useDeleteHook,
  useListHooks,
  useUpdateAgent,
  useUpdateHook,
} from '@/hooks/api';
import { isEmpty } from '@/utils';

export type UseDeleteExportOnSuccess = (action: Action) => void;
export type UseDeleteExportOnError = ({ error }: { error: Error }) => void;
export type UseDeleteExportCallbackArgs = {
  agentId: string;
  actionId: string;
  onSuccess?: UseDeleteExportOnSuccess;
  onError?: UseDeleteExportOnError;
};
export type UseDeleteExportCallback = ({
  agentId,
  actionId,
  onSuccess,
  onError,
}: UseDeleteExportCallbackArgs) => Promise<Action>;

export type UseDeleteExportOpts = {
  onSuccess?: UseDeleteExportOnSuccess;
  onError?: UseDeleteExportOnError;
};

export const useDeleteExport = ({ onSuccess: onSuccessFnBase, onError: onErrorFnBase }: UseDeleteExportOpts = {}) => {
  const { data: { data: hooks } = {} } = useListHooks();

  const {
    mutateAsync: updateAgent,
    isPending: isPendingUpdateAgent,
    isSuccess: isSuccessUpdateAgent,
    error: errorUpdateAgent,
  } = useUpdateAgent();

  const {
    mutateAsync: deleteAction,
    isPending: isPendingDeleteAction,
    isSuccess: isSuccessDeleteAction,
    error: errorDeleteAction,
  } = useDeleteAction();

  const {
    mutateAsync: deleteHook,
    isPending: isPendingDeleteHook,
    isSuccess: isSuccessDeleteHook,
    error: errorDeleteHook,
  } = useDeleteHook();

  const {
    mutateAsync: updateHook,
    isPending: isPendingUpdateHook,
    isSuccess: isSuccessUpdateHook,
    error: errorUpdateHook,
  } = useUpdateHook();

  const deleteExport: UseDeleteExportCallback = useCallback(
    async ({ agentId, actionId, onSuccess: onSuccessFn, onError: onErrorFn }: UseDeleteExportCallbackArgs) => {
      const onSuccess: UseDeleteExportOnSuccess = (...args) => {
        onSuccessFnBase?.(...args);
        onSuccessFn?.(...args);
      };
      const onError: UseDeleteExportOnError = (...args) => {
        onErrorFnBase?.(...args);
        onErrorFn?.(...args);
      };

      try {
        const deletedHookIds: string[] = [];

        for (const hook of hooks ?? []) {
          const updates: Partial<Hook> = {};

          if (hook.trueActionId === actionId) {
            updates.trueActionId = null;
          }

          if (hook.falseActionId === actionId) {
            updates.falseActionId = null;
          }

          if (!isEmpty(updates)) {
            const afterUpdate = { ...hook, ...updates } as Hook;
            if ([afterUpdate.trueActionId, afterUpdate.falseActionId].every((id) => id == null)) {
              await deleteHook(toHookId(hook.hookId));
              deletedHookIds.push(hook.hookId);
            } else {
              await updateHook({ id: toHookId(hook.hookId), updates });
            }
          }
        }

        const action = await deleteAction(toActionId(actionId));

        await updateAgent({
          id: toAgentId(agentId),
          updates: (agent: Agent) => ({
            resourceIds: [...agent.resourceIds.filter((id) => id !== actionId && !deletedHookIds.includes(id))],
          }),
        });

        onSuccess?.(action);

        return action;
      } catch (error) {
        if (error instanceof Error) {
          onError?.({ error });
        } else {
          onError?.({ error: new Error('An unknown error occurred') });
        }
        throw error;
      }
    },
    [onSuccessFnBase, onErrorFnBase, deleteAction, updateAgent, hooks, deleteHook, updateHook]
  );

  return {
    deleteExport,
    isPending: isPendingDeleteHook || isPendingUpdateHook || isPendingDeleteAction || isPendingUpdateAgent,
    isSuccess: isSuccessDeleteHook || isSuccessUpdateHook || isSuccessDeleteAction || isSuccessUpdateAgent,
    errors: [errorDeleteHook, errorUpdateHook, errorDeleteAction, errorUpdateAgent].filter((e) => !!e),
  };
};
